/**
 * @efacture/fiscal-core - Statistical report models (DGI SFE §4 and §5).
 *
 * Declares the X, Z and A report contracts and assembles them from already
 * aggregated input: French labels, DGI ordering, validation. No arithmetic.
 *
 * Contract units: an amount is a decimal STRING in FCFA ("1180", "-2360");
 * a timestamp is ISO 8601 UTC; a window is HALF-OPEN, `from` included and
 * `to` excluded, `from: null` meaning no previous report bounds it.
 *
 * @module models/reports
 */
import { INVOICE_TYPES } from "../constants/invoice-types.js";
import { TAX_GROUPS } from "../constants/tax-groups.js";
import { PAYMENT_METHODS } from "./payment.js";
/**
 * French labels of the report types.
 *
 * §4.3.d and §5.2.d require the report to carry its own nature, so it travels
 * as DATA (`typeLabel`) rather than as a header hardcoded in a printer.
 */
const REPORT_TYPE_LABELS = {
    Z: "Rapport Z (clôture)",
    X_DAILY: "Rapport X quotidien",
    X_PERIODIC: "Rapport X périodique",
    A: "Rapport A (articles)",
};
/** Heading of the bucket holding the lines that carry no category. */
export const UNCATEGORISED_LABEL = "Sans catégorie";
/** Printed name of each stock perimeter. */
export const STOCK_SCOPE_LABELS = {
    ETABLISSEMENT: "stock de l'établissement",
    ENTREPRISE: "stock de l'entreprise",
};
// ============================================================================
// Assembly
// ============================================================================
/** DGI order of invoice types; an unknown type sorts last. */
const INVOICE_TYPE_ORDER = Object.keys(INVOICE_TYPES);
/** DGI order of taxation groups (A -> P). */
const TAX_GROUP_ORDER = Object.keys(TAX_GROUPS);
/** Rank of a code in an ordered referential; unknown codes rank last. */
function rank(order, code) {
    const index = order.indexOf(code);
    return index === -1 ? order.length : index;
}
/** Compares two codes by referential order, then alphabetically. */
function byReferential(order, a, b) {
    const delta = rank(order, a) - rank(order, b);
    return delta !== 0 ? delta : a.localeCompare(b);
}
function invoiceTypeLabel(code) {
    const entry = INVOICE_TYPES[code];
    return entry?.label ?? code;
}
function taxGroupLabel(code) {
    const entry = TAX_GROUPS[code];
    return entry?.label ?? code;
}
function taxGroupRate(code) {
    const entry = TAX_GROUPS[code];
    return entry?.tvaRate ?? null;
}
function paymentMethodLabel(code) {
    const entry = PAYMENT_METHODS[code];
    return entry?.label ?? code;
}
/** Formats an ISO instant as DD/MM/YYYY HH:MM (UTC) for printed labels. */
function formatInstant(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return iso;
    }
    const pad = (n) => String(n).padStart(2, "0");
    return (`${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}` +
        ` ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`);
}
/**
 * Builds the period and its printable label (§4.3.e).
 *
 * With no lower bound - no previous report - the label says so instead of
 * showing an origin date nobody chose.
 */
function buildPeriod(from, to) {
    const label = from === null
        ? `Depuis l'origine jusqu'au ${formatInstant(to)}`
        : `Du ${formatInstant(from)} au ${formatInstant(to)}`;
    return { from, to, label };
}
/**
 * Assembles an X or Z report from already aggregated data.
 *
 * Responsibilities: French labels, DGI ordering of types and groups, period
 * construction. No amount is computed here.
 */
export function generateXZReport(input) {
    const byInvoiceType = input.byInvoiceType
        .map((typeEntry) => ({
        invoiceType: typeEntry.invoiceType,
        invoiceTypeLabel: invoiceTypeLabel(typeEntry.invoiceType),
        count: typeEntry.count,
        totalAmount: typeEntry.totalAmount,
        totalTaxable: typeEntry.totalTaxable,
        totalTax: typeEntry.totalTax,
        byTaxGroup: typeEntry.byTaxGroup
            .map((group) => ({
            taxGroup: group.taxGroup,
            taxGroupLabel: taxGroupLabel(group.taxGroup),
            taxRate: taxGroupRate(group.taxGroup),
            totalAmount: group.totalAmount,
            totalTaxable: group.totalTaxable,
            totalTax: group.totalTax,
        }))
            .sort((a, b) => byReferential(TAX_GROUP_ORDER, a.taxGroup, b.taxGroup)),
    }))
        .sort((a, b) => byReferential(INVOICE_TYPE_ORDER, a.invoiceType, b.invoiceType));
    const byPaymentMethod = input.byPaymentMethod
        .map((entry) => ({
        method: entry.method,
        methodLabel: paymentMethodLabel(entry.method),
        count: entry.count,
        total: entry.total,
    }))
        .sort((a, b) => a.method.localeCompare(b.method));
    return {
        type: input.type,
        typeLabel: REPORT_TYPE_LABELS[input.type],
        companyName: input.companyName,
        ifu: input.ifu,
        isf: input.isf,
        establishmentCode: input.establishmentCode,
        establishmentName: input.establishmentName,
        generatedAt: input.generatedAt,
        period: buildPeriod(input.periodFrom, input.periodTo),
        invoiceCount: input.invoiceCount,
        totalAmount: input.totalAmount,
        totalTaxable: input.totalTaxable,
        totalTax: input.totalTax,
        totalTVA: input.totalTVA,
        totalTSB: input.totalTSB,
        totalSpecificTax: input.totalSpecificTax ?? "0",
        totalPSVB: input.totalPSVB,
        byInvoiceType,
        byPaymentMethod,
        totalDiscounts: input.totalDiscounts,
        totalOtherReductions: input.totalOtherReductions,
        incompleteSaleCount: input.incompleteSaleCount,
        settledTTC: input.settledTTC,
        unsettledTTC: input.unsettledTTC,
        unrefundedCreditNotesTTC: input.unrefundedCreditNotesTTC,
        withheldPSVB: input.totalPSVB,
        firstInvoice: input.firstInvoice,
        lastInvoice: input.lastInvoice,
        provisionalDocuments: input.provisionalDocuments ?? { count: 0, totalTTC: "0", settledTTC: "0" },
        ...(input.nonCertifiedModality ? { nonCertifiedModality: input.nonCertifiedModality } : {}),
        ...(input.coversCertifiedDocument ? { coversCertifiedDocument: true } : {}),
        ...(input.settlementsAfterReport && input.settlementsAfterReport.length > 0
            ? {
                settlementsAfterReport: input.settlementsAfterReport
                    .map((entry) => ({ method: entry.method, methodLabel: paymentMethodLabel(entry.method), total: entry.total }))
                    .sort((a, b) => a.method.localeCompare(b.method)),
            }
            : {}),
    };
}
/**
 * The A report's statement of the provisional documents it leaves out: the
 * stock moved at the sale, the quantities count with the regularisation.
 */
function provisionalNote(count) {
    if (count === 0)
        return null;
    if (count === 1) {
        return ("1 document provisoire émis sur la période : ses quantités seront comptées dans le rapport A de la période " +
            "de sa facture de régularisation ; le stock a été mouvementé à la vente.");
    }
    return (`${count} documents provisoires émis sur la période : leurs quantités seront comptées dans le rapport A de ` +
        "la période de leur facture de régularisation ; le stock a été mouvementé à la vente.");
}
/** Printable heading of a category bucket. */
function categoryLabel(category) {
    return category === null || category.trim() === "" ? UNCATEGORISED_LABEL : category;
}
/**
 * Assembles an A report from already aggregated data.
 *
 * Articles are ordered by code, the reading order of a printed inventory;
 * categories by label, the uncategorised bucket last so it reads as a
 * remainder rather than as a category named "S...".
 */
export function generateAReport(input) {
    const articles = input.articles
        .map((article) => ({
        articleId: article.articleId,
        code: article.code,
        name: article.name,
        category: article.category ?? null,
        unitPrice: article.unitPrice,
        priceMode: article.priceMode,
        taxGroup: article.taxGroup,
        taxRate: article.taxGroup === null ? null : taxGroupRate(article.taxGroup),
        quantitySold: article.quantitySold,
        quantityReturned: article.quantityReturned,
        stockQuantity: article.stockQuantity,
        isStockManaged: article.isStockManaged,
        totalCollected: article.totalCollected,
        totalTaxable: article.totalTaxable,
        totalTax: article.totalTax,
        fiscalFieldAnomaly: article.fiscalFieldAnomaly ?? null,
    }))
        .sort((a, b) => a.code.localeCompare(b.code));
    const byCategory = (input.byCategory ?? [])
        .map((entry) => ({
        category: entry.category,
        categoryLabel: categoryLabel(entry.category),
        quantitySold: entry.quantitySold,
        quantityReturned: entry.quantityReturned,
        totalCollected: entry.totalCollected,
        totalTaxable: entry.totalTaxable,
        totalTax: entry.totalTax,
    }))
        .sort((a, b) => {
        if (a.category === null)
            return b.category === null ? 0 : 1;
        if (b.category === null)
            return -1;
        return a.categoryLabel.localeCompare(b.categoryLabel, "fr");
    });
    return {
        type: "A",
        typeLabel: REPORT_TYPE_LABELS.A,
        companyName: input.companyName,
        ifu: input.ifu,
        isf: input.isf,
        establishmentCode: input.establishmentCode,
        establishmentName: input.establishmentName,
        generatedAt: input.generatedAt,
        period: buildPeriod(input.periodFrom, input.periodTo),
        stockScope: input.stockScope,
        stockScopeLabel: STOCK_SCOPE_LABELS[input.stockScope],
        articles,
        byCategory,
        articleCount: articles.length,
        totalQuantitySold: input.totalQuantitySold,
        totalQuantityReturned: input.totalQuantityReturned,
        totalCollected: input.totalCollected,
        issuanceId: input.issuanceId,
        provisionalDocumentCount: input.provisionalDocumentCount ?? 0,
        provisionalNote: provisionalNote(input.provisionalDocumentCount ?? 0),
        ...(input.nonCertifiedModality ? { nonCertifiedModality: input.nonCertifiedModality } : {}),
        ...(input.coversCertifiedDocument ? { coversCertifiedDocument: true } : {}),
    };
}
/**
 * An X/Z report with no transaction at all: every total at zero.
 *
 * Serves a consumer that must print a regulatory report over an empty period:
 * the absence of sales is information, not a blank page.
 */
export function createEmptyXZReport(input) {
    return generateXZReport({
        ...input,
        invoiceCount: 0,
        totalAmount: "0",
        totalTaxable: "0",
        totalTax: "0",
        totalTVA: "0",
        totalTSB: "0",
        totalSpecificTax: "0",
        totalPSVB: "0",
        byInvoiceType: [],
        byPaymentMethod: [],
        totalDiscounts: "0",
        totalOtherReductions: "0",
        incompleteSaleCount: 0,
        settledTTC: "0",
        unsettledTTC: "0",
        unrefundedCreditNotesTTC: "0",
        firstInvoice: null,
        lastInvoice: null,
    });
}
/**
 * An A report with no article movement.
 */
export function createEmptyAReport(input) {
    return generateAReport({
        ...input,
        articles: [],
        totalQuantitySold: "0",
        totalQuantityReturned: "0",
        totalCollected: "0",
    });
}
// ============================================================================
// Validation
// ============================================================================
/**
 * Validates the identification fields shared by the reports (§4.3.a/b/f).
 *
 * The ISF is not checked: `null` is the legitimate state of a company the DGI
 * has not attributed one to, and a report is not invalid for that.
 */
function validateIdentificationFields(report, errors) {
    if (!report.companyName || report.companyName.trim() === "") {
        errors.push("Nom commercial requis");
    }
    if (!report.ifu || report.ifu.trim() === "") {
        errors.push("IFU requis");
    }
    if (Number.isNaN(new Date(report.generatedAt).getTime())) {
        errors.push("Date et heure de génération invalides");
    }
    if (Number.isNaN(new Date(report.period.to).getTime())) {
        errors.push("Période invalide: fin de période illisible");
    }
    if (report.period.from !== null && report.period.from > report.period.to) {
        errors.push("Période invalide: le début doit précéder la fin");
    }
}
/**
 * Validates an X or Z report (§4.3).
 *
 * The central check: every invoice-type block carries its taxation-group
 * breakdown (§4.3.h). A type with amounts but no group is a report that
 * cannot be handed over.
 */
export function validateXZReport(report) {
    const errors = [];
    validateIdentificationFields(report, errors);
    if (report.incompleteSaleCount < 0) {
        errors.push("Le nombre de ventes incomplètes ne peut pas être négatif");
    }
    for (const typeSummary of report.byInvoiceType) {
        const prefix = `Type ${typeSummary.invoiceType}`;
        if (typeSummary.count < 0) {
            errors.push(`${prefix}: le nombre de factures ne peut pas être négatif`);
        }
        if (typeSummary.byTaxGroup.length === 0) {
            errors.push(`${prefix}: ventilation par groupe de taxation manquante`);
        }
    }
    return { valid: errors.length === 0, errors };
}
/**
 * Validates an A report (§5.2).
 */
export function validateAReport(report) {
    const errors = [];
    validateIdentificationFields(report, errors);
    for (const [index, article] of report.articles.entries()) {
        const prefix = `Article ${index + 1}`;
        if (!article.code || article.code.trim() === "") {
            errors.push(`${prefix}: Code article requis`);
        }
        if (!article.name || article.name.trim() === "") {
            errors.push(`${prefix}: Nom article requis`);
        }
        if (Number(article.quantitySold) < 0) {
            errors.push(`${prefix}: La quantité vendue ne peut pas être négative`);
        }
        if (Number(article.quantityReturned) < 0) {
            errors.push(`${prefix}: La quantité retournée ne peut pas être négative`);
        }
    }
    return { valid: errors.length === 0, errors };
}
//# sourceMappingURL=reports.js.map